import * as amplitude from "@amplitude/analytics-browser";
import { useCallback, useMemo, useState } from "react";
import { useContentConversion } from "src/components/WysiwygEditor/lexical/hooks/useContentConversion";
import { useHTMLConfig } from "src/components/WysiwygEditor/lexical/hooks/useHTMLConfig";
import AllNodes from "src/components/WysiwygEditor/lexical/nodes/AllNodes";
import { createHeadlessEditor } from "src/components/WysiwygEditor/lexical/utils/headlessEditor";
import { MessageContextSchema } from "src/generated/hooks/avela-ai/requests";
import { useAbortController } from "src/hooks/useAbortController";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import { usePrompt } from "src/hooks/usePrompt";
import { translateMessages } from "src/services/messageTranslation";
import { TranslationPreviewDrawer } from "../components/TranslationPreviewDrawer";
import {
  TranslationProgressDrawer,
  useTranslationProgress,
} from "../components/TranslationProgressDrawer";
import { AutomaticMessageFormType, MessageTranslationType } from "../schemas";
import { useLexicalEditorSettings } from "./useLexicalEditorSettings";

export function useMessageTranslation(messageContext: MessageContextSchema) {
  const getLanguageName = useGetEnglishLanguageName();
  const [isTranslating, setIsTranslating] = useState(false);
  const { emailEditorSettings } = useLexicalEditorSettings(
    messageContext.trigger_event_name
  );

  const getOriginalMessage = useCallback((values: AutomaticMessageFormType) => {
    return values.messages.find((message) => message.language === "en");
  }, []);

  const htmlConfig = useHTMLConfig({
    variableMode: "collapsed",
    ...emailEditorSettings,
  });

  const converter = useContentConversion({
    variableMode: "collapsed",
    ...emailEditorSettings,
  });

  const headlessEditor = useMemo(
    () =>
      createHeadlessEditor({
        nodes: AllNodes,
        html: htmlConfig,
        theme: {},
      }),
    [htmlConfig]
  );

  const translationProgress = useTranslationProgress();
  const {
    prompt: confirmTranslation,
    resolve: resolveTranslation,
    promptProps: translationPromptProps,
  } = usePrompt<MessageTranslationType[], MessageTranslationType[]>();

  const {
    createController,
    cancel: abortCancel,
    isAborted,
    clearRef,
  } = useAbortController();

  const cancelTranslation = useCallback(() => {
    abortCancel(translationProgress.cancel);
  }, [abortCancel, translationProgress.cancel]);

  const handleTranslate = useCallback(
    async (
      values: AutomaticMessageFormType,
      targetLanguages: string[]
    ): Promise<MessageTranslationType[] | null | undefined> => {
      const controller = createController();

      try {
        setIsTranslating(true);
        translationProgress.onOpen();
        translationProgress.onPreparations();

        const originalMessage = getOriginalMessage(values);
        if (!originalMessage) {
          throw new Error("Original message is undefined");
        }
        const originalEmailLexical = converter.parse(originalMessage.lexical);

        const { html, attachments } = converter.serialized.toHtmlWithCIDs(
          headlessEditor,
          originalEmailLexical
        );

        const originalSmsLexical = converter.parse(originalMessage.smsLexical);
        const smsText = converter.serialized.toPlainText(
          headlessEditor,
          originalSmsLexical
        );

        translationProgress.onTranslating();
        const responses = await translateMessages(
          targetLanguages,
          originalMessage.subject ?? "",
          html,
          smsText ?? "",
          messageContext,
          controller.signal
        );

        if (isAborted(controller)) {
          return undefined;
        }

        translationProgress.onFinishing();

        // strip the english translation from the response if AI generated it
        const translations = responses
          .filter((response) => response.translated_language !== "en")
          .map((response): MessageTranslationType => {
            if (response.status === "rejected") {
              return {
                error_message: response.reason,
                subject: "",
                lexical: converter.serialized.fromHtml(
                  headlessEditor,
                  "<p></p>"
                ),
                smsLexical: converter.serialized.fromPlainText(
                  headlessEditor,
                  ""
                ),
                language: response.translated_language,
                language_name: getLanguageName(response.translated_language),
              };
            }

            const translatedEmailLexical = response.email
              ? converter.restoreCIDs(
                  converter.serialized.fromHtml(headlessEditor, response.email),
                  attachments
                )
              : undefined;

            const emailLexicalWithOriginal = converter.merge(
              translatedEmailLexical,
              originalEmailLexical
            );

            const smsLexical = response.sms
              ? converter.serialized.fromPlainText(headlessEditor, response.sms)
              : undefined;

            return {
              subject: response.subject ?? "",
              lexical: emailLexicalWithOriginal,
              smsLexical: smsLexical,
              language: response.translated_language,
              language_name: getLanguageName(response.translated_language),
            };
          });

        translationProgress.onCompleted();
        translationProgress.onClose();

        const confirmedTranslations = await confirmTranslation([
          {
            language: "en",
            language_name: "English",
            subject: originalMessage.subject ?? "",
            lexical: originalEmailLexical,
            smsLexical: originalSmsLexical,
          },
          ...translations,
        ]);

        return confirmedTranslations;
      } catch (error) {
        if (isAborted(controller)) {
          amplitude.track("Clicked abort ad-hoc translation", {
            ...messageContext,
            translatable_languages: targetLanguages,
          });

          return undefined;
        }
        throw error;
      } finally {
        clearRef();
        setIsTranslating(false);
        translationProgress.onClose();
      }
    },
    [
      translationProgress,
      getOriginalMessage,
      converter,
      headlessEditor,
      messageContext,
      confirmTranslation,
      getLanguageName,
      createController,
      isAborted,
      clearRef,
    ]
  );

  return {
    isTranslating,
    handleTranslate,
    progressDrawer: (
      <TranslationProgressDrawer
        isOpen={translationProgress.isOpen}
        onClose={cancelTranslation}
        currentStep={translationProgress.currentStep}
      />
    ),
    previewDrawer: (
      <TranslationPreviewDrawer
        isOpen={!!translationPromptProps}
        translations={translationPromptProps ?? []}
        onConfirm={resolveTranslation}
        onCancel={() => resolveTranslation(null)}
        messageContext={messageContext}
      />
    ),
  };
}
